import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./TourDetail.css";
import Navbar from "../Navbar";
import Footer from "../Footer";

// ── path adjust karo apne project structure ke hisaab se ──
// e.g. agar Tours.jsx ke saath same folder mein hai toh:
// import { db } from "../firebase";
// import Navbar from "./Navbar";
// import Footer from "./Footer";

const TourDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tour, setTour] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [heroImg, setHeroImg] = useState(0); // carousel index for hero

  // ── Fetch tour doc ──
  useEffect(() => {
    const fetchTour = async () => {
      try {
        const snap = await getDoc(doc(db, "tours", id));
        if (snap.exists()) {
          setTour({ id: snap.id, ...snap.data() });
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error("Error fetching tour:", err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchTour();
  }, [id]);

  // ── Fetch packages once tour is loaded ──
  useEffect(() => {
    if (!tour) return;
    const fetchPackages = async () => {
      try {
        const pkgsQ = query(
          collection(db, "tours", id, "packages"),
          where("isActive", "==", true),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(pkgsQ);
        setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("Error fetching packages:", err);
      }
    };
    fetchPackages();
  }, [tour, id]);

  // ── Reading progress bar ──
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setReadingProgress(Math.min(progress, 100));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Scroll to top on load ──
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const handleEnquire = (pkg) => {
    window.open(
      `https://wa.me/919096785868?text=Hi! I'm interested in the "${pkg.packageName}" package from the "${tour.tourName}" tour.`,
      "_blank"
    );
  };

  const handleGeneralEnquire = () => {
    window.open(
      `https://wa.me/919096785868?text=Hi! I'm interested in the "${tour.tourName}" tour.`,
      "_blank"
    );
  };

  // ── LOADING ──
  if (loading) {
    return (
      <div className="td-loading-screen">
        <div className="td-loading-spinner"></div>
        <p>Loading tour details...</p>
      </div>
    );
  }

  // ── NOT FOUND ──
  if (notFound || !tour) {
    return (
      <div className="td-notfound">
        <i className="fas fa-map-signs"></i>
        <h2>Tour not found</h2>
        <p>This tour may have been removed or the link is invalid.</p>
        <button onClick={() => navigate("/tours")}>
          <i className="fas fa-arrow-left"></i> Back to Tours
        </button>
      </div>
    );
  }

  const images = tour.imageUrls || [];

  return (
    <div className="td-page">
      {/* Reading Progress Bar */}
      <div
        className="td-progress-bar"
        style={{ width: `${readingProgress}%` }}
      />

      {/* ── NAVBAR ── */}
      <Navbar />

      {/* ── HERO ── */}
      <header className="td-hero">
        {images.length > 0 ? (
          <div className="td-hero-carousel">
            <img
              className="td-hero-carousel-img"
              src={images[heroImg]}
              alt={tour.tourName}
            />
            {images.length > 1 && (
              <>
                <button
                  className="td-hero-carousel-btn prev"
                  onClick={() =>
                    setHeroImg((c) => (c - 1 + images.length) % images.length)
                  }
                >
                  ‹
                </button>
                <button
                  className="td-hero-carousel-btn next"
                  onClick={() =>
                    setHeroImg((c) => (c + 1) % images.length)
                  }
                >
                  ›
                </button>
                <div className="td-hero-carousel-dots">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      className={`td-hero-dot${i === heroImg ? " active" : ""}`}
                      onClick={() => setHeroImg(i)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="td-hero-placeholder">🧳</div>
        )}

        <div className="td-hero-overlay">
          {/* Back button */}
          <button
            className="td-back-btn"
            onClick={() => navigate("/tours")}
          >
            <i className="fas fa-arrow-left"></i> All Tours
          </button>

          {/* Tags */}
          <div className="td-hero-tags">
            {packages.length > 0 && (
              <span className="td-hero-tag">
                🗺️ {packages.length} Package{packages.length > 1 ? "s" : ""} Available
              </span>
            )}
            {tour.duration && (
              <span className="td-hero-tag">🕐 {tour.duration}</span>
            )}
            {tour.destination && (
              <span className="td-hero-tag">📍 {tour.destination}</span>
            )}
          </div>

          <h1 className="td-hero-title">{tour.tourName}</h1>

          <div className="td-hero-meta">
            {tour.tagline && <span>✨ "{tour.tagline}"</span>}
          </div>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="td-main">
        <div className="td-content-wrap">

          {/* Tagline quote */}
          {tour.tagline && (
            <blockquote className="td-quote">
              <i className="fas fa-quote-left td-quote-icon"></i>
              {tour.tagline}
            </blockquote>
          )}

          {/* Description */}
          {tour.description && (
            <div className="td-body">
              {tour.description.split("\n").map((para, i) =>
                para.trim() ? <p key={i}>{para}</p> : <br key={i} />
              )}
            </div>
          )}

          {/* Highlights */}
          {tour.highlights?.length > 0 && (
            <div className="td-section">
              <h3 className="td-section-title">
                <i className="fas fa-star"></i> Highlights
              </h3>
              <ul className="td-list">
                {tour.highlights.map((h, i) => (
                  <li key={i}>
                    <span className="icon">⭐</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Things To Do */}
          {tour.thingsToDo?.length > 0 && (
            <div className="td-section">
              <h3 className="td-section-title">
                <i className="fas fa-hiking"></i> Things To Do
              </h3>
              <ul className="td-list">
                {tour.thingsToDo.map((t, i) => (
                  <li key={i}>
                    <span className="icon">✓</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Travel Tips */}
          {tour.travelTips?.length > 0 && (
            <div className="td-section">
              <h3 className="td-section-title">
                <i className="fas fa-lightbulb"></i> Travel Tips
              </h3>
              <ul className="td-list">
                {tour.travelTips.map((t, i) => (
                  <li key={i}>
                    <span className="icon">💡</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── PACKAGES ── */}
          <div className="td-packages-section">
            <h3 className="td-section-title">
              <i className="fas fa-box-open"></i> Available Packages
            </h3>

            {packages.length === 0 ? (
              <div className="td-no-packages">
                <p>Interested in this tour?</p>
                <button onClick={handleGeneralEnquire}>
                  💬 Enquire on WhatsApp
                </button>
              </div>
            ) : (
              <div className="td-pkg-grid">
                {packages.map((pkg) => (
                  <div className="td-pkg-card" key={pkg.id}>

                    {/* Price bar */}
                    <div className="td-pkg-price-bar">
                      {pkg.discountPrice ? (
                        <>
                          <span className="td-pkg-disc">
                            ₹{Number(pkg.discountPrice).toLocaleString("en-IN")}
                          </span>
                          <span className="td-pkg-orig">
                            ₹{Number(pkg.price).toLocaleString("en-IN")}
                          </span>
                          <span className="td-pkg-save">
                            Save ₹
                            {(
                              Number(pkg.price) - Number(pkg.discountPrice)
                            ).toLocaleString("en-IN")}
                          </span>
                        </>
                      ) : (
                        <span className="td-pkg-disc">
                          {pkg.price
                            ? `₹${Number(pkg.price).toLocaleString("en-IN")}`
                            : "Price on request"}
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <div className="td-pkg-body">
                      <h4 className="td-pkg-name">{pkg.packageName}</h4>

                      <div className="td-pkg-chips">
                        {pkg.destination && (
                          <span className="td-pkg-chip">📍 {pkg.destination}</span>
                        )}
                        {pkg.duration && (
                          <span className="td-pkg-chip">🕐 {pkg.duration}</span>
                        )}
                      </div>

                      {pkg.description && (
                        <p className="td-pkg-desc">{pkg.description}</p>
                      )}

                      {pkg.itinerary && (
                        <div className="td-pkg-itinerary">
                          <span className="td-pkg-itinerary-label">
                            🗓️ Itinerary
                          </span>
                          {pkg.itinerary}
                        </div>
                      )}

                      {pkg.inclusions && (
                        <div className="td-pkg-inc">
                          <span className="td-pkg-inc-label">✅ Inclusions</span>
                          <span className="td-pkg-inc-text">
                            {pkg.inclusions}
                          </span>
                        </div>
                      )}

                      {pkg.exclusions && (
                        <div className="td-pkg-exc">
                          <span className="td-pkg-exc-label">❌ Exclusions</span>
                          <span className="td-pkg-exc-text">
                            {pkg.exclusions}
                          </span>
                        </div>
                      )}

                      <button
                        className="td-pkg-btn"
                        onClick={() => handleEnquire(pkg)}
                      >
                        <img
                          src="/assets/images/whatsapp.png"
                          alt="WhatsApp"
                          style={{ width: 18, height: 18, objectFit: "contain" }}
                        />
                        Enquire Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Back to tours */}
          <div className="td-back-row">
            <button
              className="td-back-all-btn"
              onClick={() => navigate("/tours")}
            >
              <i className="fas fa-arrow-left"></i> Back to All Tours
            </button>
          </div>
        </div>
      </main>

      {/* ── FOOTER ── */}
      <Footer />
    </div>
  );
};

export default TourDetail;